import React, {useEffect, useMemo, useState} from 'react'
import styles from './UIGameTradeOfferActiveOffer.module.scss'
import {CardEnum, type CardStateUnordered, PlayerColor, type TradeOfferState, TradeResponseStatus} from '@colonist/game-states'
import {TradeValidator} from '@colonist/game-mechanic-validators'
import {UIGameCardInventory} from '../../../common/UIGameCardInventory/UIGameCardInventory.tsx'
import IconTradeArrowGreen from '@static/images/icon_trade_arrow_green.svg'
import IconTradeArrowRed from '@static/images/icon_trade_arrow_red.svg'
import {Avatar} from '../../../common/Avatar/index.ts'
import {LobbyImage} from '@colonist/types'
import {UIGameTradeOfferRejectTradeButton} from '../UIGameTradeOfferRejectTradeButton/index.ts'
import {UIGameTradeOfferAcceptTradeButton} from '../UIGameTradeOfferAcceptTradeButton/index.ts'
import {UIGameTradeOfferCounterOfferButton} from '../UIGameTradeOfferCounterOfferButton/index.ts'
import {UIGameTradeOfferRespondTradeButton} from '../UIGameTradeOfferRespondTradeButton/index.ts'
import clsx from 'clsx'
import {Sound} from '../../../utils/sound.ts'
import {type TranslationKeys, type TranslationObject} from '@colonist/i18n'
import {T, Tooltip} from '@colonist/ui'
import {cardEnumsToCardState} from '../../../utils/cards.ts'
import {useGameStore} from '../../../store/GameStoreContext.ts'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators.ts'
import {TradeEmbargoControllerReader} from '@colonist/game-base-types'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator.ts'

const getCardsToDisable = (myCardState: CardStateUnordered, requiredCardState: CardStateUnordered) => {
    const cardsToDisable: CardStateUnordered = {}
    for(const cardEnum of Object.keys(requiredCardState)) {
        const myCardCount = myCardState[cardEnum] ?? 0
        const requiredCardCount = requiredCardState[cardEnum]
        const numberOfCardsToDisable = requiredCardCount - myCardCount
        const isAnyResourceCard = Number(cardEnum) === CardEnum.AnyResource
        if(numberOfCardsToDisable > 0 && !isAnyResourceCard) cardsToDisable[cardEnum] = numberOfCardsToDisable
    }
    return cardsToDisable
}

const getAvatarTradeStatusTooltipKey = (tradeStatus: TradeResponseStatus): TranslationKeys => {
    switch(tradeStatus) {
        case TradeResponseStatus.Accepted: return 'strings:game.tooltips.tradeOfferPlayerAccepted'
        case TradeResponseStatus.Rejected: return 'strings:game.tooltips.tradeOfferPlayerRejected'
        case TradeResponseStatus.PendingResponse: return 'strings:game.tooltips.tradeOfferPlayerPending'
        case TradeResponseStatus.Embargoed: return 'strings:game.tooltips.tradeOfferPlayerBlockedTrading'
    }
}

export const UIGameTradeOfferActiveOfferReceivingHalf = ({
    tradeValidator,
    disableTooltips,
}: {
    tradeValidator: TradeValidator
    disableTooltips?: boolean | undefined
}) => {
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)
    const myColor = useGameStore((state) => state.myColor)
    const tradeState = useGameStore((state) => state.gameState.tradeState)
    const {playerValidators, getPlayerValidator} = usePlayerValidators()
    const recipientColor = tradeValidator.getRecipientColor(myColor)
    const isAllPlayersIcon = recipientColor === PlayerColor.None
    const recipient = !isAllPlayersIcon ? getPlayerValidator(recipientColor) : null
    const receivingCardEnums = tradeValidator.getReceivingResources(myColor)
    const receivingCards = useMemo(() => cardEnumsToCardState(receivingCardEnums), [receivingCardEnums])
    const amITradeExecutor = tradeValidator.tradeExecutorColor == myColor

    const playersExceptRecipientAndMe = playerValidators.filter((playerValidator) => {
        const playerColor = playerValidator.color
        if(playerColor === tradeValidator.getRecipientColor(myColor)) return false
        if(playerColor === myColor) return false
        if(isMyColorSpectator && playerColor === myColor) return false
        return true
    })

    const shouldShowAvatarsWithStatus = tradeValidator.isOpenEndedTrade() || !amITradeExecutor || isMyColorSpectator

    return (<div className={styles.receivingHalfContainer}>
        <div className={styles.leftContainer}>
            {recipient ? <Avatar
                image={recipient.displayIcon}
                color={recipientColor}
                profilePictureUrl={recipient.profilePictureUrl}
                className={styles.avatar}
            /> : <Avatar
                image={LobbyImage.Players}
                hideBackground
                className={styles.avatar}
            />}

            <img src={IconTradeArrowGreen} className={styles.receivingArrow}/>
            <UIGameCardInventory
                cardState={receivingCards}
                animate={false}
                enablePartition={false}
                cardSize='sm'
                className={styles.cardStack} />
        </div>
        <div className={styles.rightContainer}>
            {shouldShowAvatarsWithStatus && playersExceptRecipientAndMe.map((player) => {
                const playerResponseState = tradeValidator.getPlayerResponseState(player.color)
                if((playerResponseState?.responseStatus) == null) return null
                const isEmbargoed = playerResponseState?.responseStatus === TradeResponseStatus.Embargoed

                let playerUsername = player.username
                let recipientUsername = recipient?.username ?? ''

                if(isEmbargoed && recipient) {
                    const isPlayer1EmbargoedRecipient = TradeEmbargoControllerReader
                        .doesPlayer1HaveAnEmbargoAgainstPlayer2(tradeState.embargoState, player.color, recipientColor)

                    if(!isPlayer1EmbargoedRecipient) {
                        playerUsername = recipient.username
                        recipientUsername = player.username
                    }
                }
                const tooltipText: TranslationObject = {
                    key: getAvatarTradeStatusTooltipKey(playerResponseState.responseStatus),
                    options: {
                        playerUsername: playerUsername,
                        recipientPlayerName: recipientUsername,
                    },
                }
                return (
                    <Tooltip key={player.color} content={<T id={tooltipText}/>} disabled={disableTooltips} centerText>
                        <Avatar
                            image={player.displayIcon}
                            color={player.color}
                            profilePictureUrl={player.profilePictureUrl}
                            className={styles.avatar}
                            tradeResponseStatus={playerResponseState.responseStatus}
                            isEmbargoed={isEmbargoed}
                        />
                    </Tooltip>

                )
            })}
        </div>
    </div>

    )
}

export const UIGameTradeOfferActiveOfferGivingHalf = ({
    tradeValidator,
    isGameInBackground,
    disableTooltips,
}: {
    tradeValidator: TradeValidator
    isGameInBackground: boolean
    disableTooltips?: boolean | undefined
}) => {
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)
    const myColor = useGameStore((state) => state.myColor)
    const {getPlayerValidator} = usePlayerValidators()
    const [isOtherButtonsClicked, setIsOtherButtonsClicked] = useState(false)

    const amITradeExecutor = tradeValidator.tradeExecutorColor == myColor
    const givingColor = myColor
    const givingPlayerValidator = !isMyColorSpectator ? getPlayerValidator(givingColor) : null
    const givingCardEnums = tradeValidator.getGivingResources(myColor)
    const givingCards = useMemo(() => cardEnumsToCardState(givingCardEnums), [givingCardEnums])

    const cardsToDisable = useMemo(() => {
        if(!givingPlayerValidator) return {}
        const myCardsInventory = cardEnumsToCardState(givingPlayerValidator.resourceCards.cards)
        return getCardsToDisable(myCardsInventory, givingCards)
    }, [givingCards, givingPlayerValidator])

    const playerResponseButtons = useMemo(() => {
        if(!amITradeExecutor) return null
        const playerResponseStates = tradeValidator.getPlayerResponseStates()
        return playerResponseStates.map((playerResponse) => <UIGameTradeOfferRespondTradeButton
            key={playerResponse.player}
            playerResponse={playerResponse}
            tradeValidator={tradeValidator}
            disableTooltips={disableTooltips}
        />)
    }, [tradeValidator, amITradeExecutor, disableTooltips])

    return (
        <div className={styles.givingHalfContainer}>
            <div className={styles.leftContainer}>
                {givingPlayerValidator ? (
                    <Avatar
                        image={givingPlayerValidator.displayIcon}
                        color={givingColor}
                        profilePictureUrl={givingPlayerValidator.profilePictureUrl}
                        className={styles.avatar}
                    />
                ) : (
                    <Avatar
                        image={LobbyImage.Players}
                        hideBackground
                        className={styles.avatar}
                    />
                )}
                <img src={IconTradeArrowRed} className={styles.givingArrow}/>
                <UIGameCardInventory
                    cardsToDisable={cardsToDisable}
                    cardState={givingCards}
                    animate={false}
                    enablePartition={false}
                    cardSize='sm'
                    className={styles.cardStack} />
            </div>
            <div className={styles.rightContainer}>
                {playerResponseButtons}
                <UIGameTradeOfferCounterOfferButton
                    tradeValidator={tradeValidator}
                    setIsOtherButtonsClicked={setIsOtherButtonsClicked}
                    disableTooltips={disableTooltips}
                />
                <UIGameTradeOfferRejectTradeButton
                    setIsOtherButtonsClicked={setIsOtherButtonsClicked}
                    isOtherButtonsClicked={isOtherButtonsClicked}
                    tradeValidator={tradeValidator}
                    isGameInBackground={isGameInBackground}
                    disableTooltips={disableTooltips}
                />
                <UIGameTradeOfferAcceptTradeButton
                    setIsOtherButtonsClicked={setIsOtherButtonsClicked}
                    tradeValidator={tradeValidator}
                    disableTooltips={disableTooltips}
                />
            </div>
        </div>
    )
}

export interface UIGameTradeOfferActiveOfferProps {
    offer: TradeOfferState
    className?: string | undefined
    isGameInBackground: boolean
    disableTooltips?: boolean | undefined
}

export const UIGameTradeOfferActiveOffer = ({offer, className, isGameInBackground, disableTooltips}: UIGameTradeOfferActiveOfferProps) => {
    const myColor = useGameStore((state) => state.myColor)
    const {playerValidators, getPlayerValidator} = usePlayerValidators()
    const tradeState = useGameStore((state) => state.gameState.tradeState)
    const currentStateValidator = useCurrentStateValidator()

    const tradeValidator = new TradeValidator(offer,
        currentStateValidator.currentTurnPlayers,
        playerValidators,
        tradeState.embargoState)
    const isCounterOffer: boolean = offer.counterOfferInResponseToTradeId != undefined
    const creatorValidator = getPlayerValidator(offer.creator)

    useEffect(() => {
        const canIAcceptTrade = tradeValidator.getAcceptTradeActionStatus(myColor).canTakeAction
        const soundToPlay = canIAcceptTrade ? Sound.SoundType.OfferAcceptable : Sound.SoundType.OfferNotAcceptable
        Sound.playSound(soundToPlay)
    }, [])

    return (
        <div className={clsx(styles.container, className)}>
            {isCounterOffer && <div className={styles.counterOfferContainer}>
                <Avatar
                    image={creatorValidator.displayIcon}
                    color={creatorValidator.color}
                    profilePictureUrl={creatorValidator.profilePictureUrl}
                    className={styles.avatar}
                />
            </div>}

            <div className={styles.tradeContainer}>
                <UIGameTradeOfferActiveOfferReceivingHalf
                    tradeValidator={tradeValidator}
                    disableTooltips={disableTooltips}/>
                <UIGameTradeOfferActiveOfferGivingHalf
                    tradeValidator={tradeValidator}
                    isGameInBackground={isGameInBackground}
                    disableTooltips={disableTooltips}/>
            </div>
        </div>
    )
}
